import { useAddressInput, useComprehensiveTopNav, useCustomToast } from 'hooks';
import routes from 'lib/api/routes';
import { Button, Typography } from 'lib/components';
import { TextField, TimezoneSelect } from 'lib/components/FormFields';
import WorkingHourInputField from 'lib/components/WorkingHourInputField';
import { Day, DayIndex, DayItem, Time, WorkingHoursFactory } from 'lib/components/WorkingHourInputField/types';
import WorkingHourModal from 'lib/components/WorkingHourModal';
import { RateType } from 'lib/util';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { actions, RequestManager, useAppDispatch, useSelectRequest } from 'store';
import BuddyPunchLocationSelect from '../../lib/components/FormFields/CustomSelect/BuddyPunchLocationSelect';
import { BuddyPunchLocation, HospitalWorkingHours } from '../../types/entity';
import classes from './index.module.scss';

/**
 * Symbols for number inputs ($ for fixed amounts, % for percentages)
 */
const NUMBER_SYMBOL = ['$', '%'];

/**
 * Initial working hours configuration for all days of the week
 * Sets up default hours (9:00 AM to 9:00 PM) with isOperating=false
 */
const WORKING_HOURS_FACTORY_INIT = [
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
	'Sunday',
].reduce<WorkingHoursFactory>(
	(acc, cur) => {
		acc.days.push({
			day: cur as Day,
			isOperating: false,
			startTime: { hr: '09', min: '00', pm: false },
			endTime: { hr: '09', min: '00', pm: true },
		});
		return acc;
	},
	{ days: [], selectedIndex: 0 }
);

/**
 * HospitalNewScreen component
 * Screen for creating a new hospital
 * Displays a form with multiple sections for hospital details, working hours, and pricing
 *
 * @returns {JSX.Element} - Rendered component
 */
const HospitalNewScreen = () => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Navigation helper
	const { push } = useHistory();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();

	// Basic hospital information form state
	const [name, setName] = useState('');
	const [timezone, setTimeZone] = useState<string>();
	const [email, setEmail] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [employeesAmount, setEmployeesAmount] = useState(0);
	const [payoutId, setPayoutId] = useState('');

	// Pricing rates form state
	const [regularRate, setRegularRate] = useState(0);
	const [overtimeRate, setOvertimeRate] = useState(0);
	const [weekendRate, setWeekendRate] = useState(0);
	const [holidayRate, setHolidayRate] = useState(0);

	// Rate types (fixed amount or percentage)
	const [overtimeRateTypeId, setOvertimeRateTypeId] = useState<RateType>(0);
	const [weekendRateTypeId, setWeekendRateTypeId] = useState<RateType>(0);
	const [holidayRateTypeId, setHolidayRateTypeId] = useState<RateType>(0);

	// BuddyPunch integration
	const [buddyPunchId, setBuddyPunchId] = useState<BuddyPunchLocation | undefined>(undefined);

	// Cancellation policy percentages with default values
	const [beforeDispatch, setBeforeDispatch] = useState<number>(0);
	const [afterDispatch, setAfterDispatch] = useState<number>(25);
	const [beforeTreatment, setBeforeTreatment] = useState<number>(50);
	const [duringTreatment, setDuringTreatment] = useState<number>(100);

	// Working hours configuration state
	const [workingHoursFactory, setWorkingHoursFactory] = useState<WorkingHoursFactory>(WORKING_HOURS_FACTORY_INIT);
	// Controls visibility of the working hours modal
	const [workingHourModalOpen, setWorkingHourModalOpen] = useState(false);

	/**
	 * Navigate to the next day in the working hours modal
	 * Cycles back to Monday after Sunday
	 */
	const nextWorkingDay = () => {
		setWorkingHoursFactory((prev) => {
			const newIndex = prev.selectedIndex + 1 > 6 ? 0 : prev.selectedIndex + 1;
			return {
				days: prev.days.map((day) => ({ ...day })),
				selectedIndex: newIndex as DayIndex,
			};
		});
	};

	/**
	 * Navigate to the previous day in the working hours modal
	 * Cycles to Sunday after Monday
	 */
	const prevWorkingDay = () =>
		setWorkingHoursFactory((prev) => {
			const newIndex = prev.selectedIndex - 1 < 0 ? 6 : prev.selectedIndex - 1;
			return {
				days: prev.days.map((day) => ({ ...day })),
				selectedIndex: newIndex as DayIndex,
			};
		});

	/**
	 * Update working hours for a specific day when confirmed in the modal
	 *
	 * @param {DayItem} dayItem - The updated day configuration
	 */
	const handleWorkingHourModalOnConfirmed = (dayItem: DayItem) => {
		setWorkingHoursFactory((prev) => {
			// Find and update the specific day that was changed
			const newDays = [...prev.days].map((item) => {
				if (item.day === dayItem.day) {
					return { ...dayItem };
				}
				return item;
			});
			return {
				...prev,
				days: newDays,
			};
		});
	};

	/**
	 * Set the currently selected day in the working hours configuration
	 *
	 * @param {DayIndex} index - Index of the day to select (0-6)
	 */
	const setWorkingHoursDayIndex = (index: DayIndex) => {
		setWorkingHoursFactory((prev) => {
			return {
				days: prev.days.map((day) => ({ ...day })),
				selectedIndex: index,
			};
		});
	};

	/**
	 * Handle click on a working hour field to open the modal
	 * Sets the selected day to the clicked day
	 *
	 * @param {number} index - Index of the clicked day
	 */
	const handleWorkingHourFieldClick = (index: number) => {
		setWorkingHourModalOpen((prev) => {
			setWorkingHoursDayIndex(index as DayIndex);
			return !prev;
		});
	};

	/**
	 * Custom hook for address input fields
	 * Provides state and handlers for all address-related fields
	 */
	const {
		addressLine1,
		setAddressLine1,
		addressLine2,
		setAddressLine2,
		city,
		setCity,
		state,
		setState,
		postalCode,
		setPostalCode,
	} = useAddressInput();

	/**
	 * Monitor API requests for hospital creation
	 * Shows toast notifications for success/failure and redirects on success
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.HOSPITAL, 'POST')) {
			if (RM.failed()) {
				// Show error message
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				// Show success message and redirect to hospitals list
				toast(active.message);
				push('/hospitals');
			}
		}
	}, [active]);

	/**
	 * Format time for working hours
	 * Converts Time object to string format or null if not operating
	 *
	 * @param {Time} entry - Time object with hour, minute, and AM/PM
	 * @param {boolean} isOperating - Whether the hospital is operating at this time
	 * @returns {string | null} - Formatted time string or null
	 */
	const formatDay = useCallback(
		(entry: Time, isOperating: boolean) => {
			return isOperating ? `${entry.hr}:${entry.min} ${entry.pm ? 'PM' : 'AM'}` : null;
		},
		[workingHoursFactory]
	);

	/**
	 * Create a new hospital with the form data
	 * Formats working hours and dispatches the addHospital action
	 */
	const createHospital = () => {
		// Format working hours for all days of the week
		const workingHours: HospitalWorkingHours = {
			monFrom: formatDay(workingHoursFactory.days[0].startTime, workingHoursFactory.days[0].isOperating),
			monTo: formatDay(workingHoursFactory.days[0].endTime, workingHoursFactory.days[0].isOperating),
			tueFrom: formatDay(workingHoursFactory.days[1].startTime, workingHoursFactory.days[1].isOperating),
			tueTo: formatDay(workingHoursFactory.days[1].endTime, workingHoursFactory.days[1].isOperating),
			wedFrom: formatDay(workingHoursFactory.days[2].startTime, workingHoursFactory.days[2].isOperating),
			wedTo: formatDay(workingHoursFactory.days[2].endTime, workingHoursFactory.days[2].isOperating),
			thuFrom: formatDay(workingHoursFactory.days[3].startTime, workingHoursFactory.days[3].isOperating),
			thuTo: formatDay(workingHoursFactory.days[3].endTime, workingHoursFactory.days[3].isOperating),
			friFrom: formatDay(workingHoursFactory.days[4].startTime, workingHoursFactory.days[4].isOperating),
			friTo: formatDay(workingHoursFactory.days[4].endTime, workingHoursFactory.days[4].isOperating),
			satFrom: formatDay(workingHoursFactory.days[5].startTime, workingHoursFactory.days[5].isOperating),
			satTo: formatDay(workingHoursFactory.days[5].endTime, workingHoursFactory.days[5].isOperating),
			sunFrom: formatDay(workingHoursFactory.days[6].startTime, workingHoursFactory.days[6].isOperating),
			sunTo: formatDay(workingHoursFactory.days[6].endTime, workingHoursFactory.days[6].isOperating),
		};

		// Dispatch action to create hospital with all form data
		dispatch(
			actions.addHospital({
				// Basic information
				name,
				email,
				// Address information
				addressLine1,
				addressLine2: addressLine2 === '' ? undefined : addressLine2,
				city,
				state,
				postalCode,
				phoneNumber,
				// Integration IDs
				payoutId: payoutId !== '' ? payoutId : undefined,
				buddyPunchId: buddyPunchId !== undefined ? buddyPunchId.id.toString() : undefined,
				// Rate information
				regularRate,
				overtimeRate,
				weekendRate,
				holidayRate,
				overtimeRateTypeId,
				weekendRateTypeId,
				holidayRateTypeId,
				// Other details
				employeesAmount: employeesAmount !== 0 ? employeesAmount : undefined,
				timezone: timezone!,
				workingHours,
				// Cancellation policy
				cancellationPolicy: {
					beforeDispatch,
					afterDispatch,
					beforeTreatment,
					duringTreatment,
				},
			})
		);
	};

	/**
	 * Handle rate type symbol change ($ or %)
	 * Updates the appropriate rate type state based on the field
	 *
	 * @param {string} field - Which rate field is being updated
	 * @param {RateType} symbol - The new rate type (0 for fixed, 1 for percentage)
	 */
	const handleSymbolChange = (field: 'overtime' | 'weekend' | 'holiday', symbol: RateType) => {
		if (field === 'overtime') setOvertimeRateTypeId(symbol);
		else if (field === 'weekend') setWeekendRateTypeId(symbol);
		else setHolidayRateTypeId(symbol);
	};

	// Set up top navigation with page title
	useComprehensiveTopNav('Create New Hospital Page');

	/**
	 * Fetch BuddyPunch locations on component mount
	 * These are used in the BuddyPunch location dropdown
	 */
	React.useEffect(() => {
		dispatch(actions.getBuddyPunchLocations());
	}, []);

	return (
		<>
			{/* Working hours modal - shown when a working hour field is clicked */}
			<WorkingHourModal
				isOpen={workingHourModalOpen}
				workingHoursFactory={workingHoursFactory}
				onClose={() => setWorkingHourModalOpen(false)}
				onNextDay={nextWorkingDay}
				onPrevDay={prevWorkingDay}
				onConfirmed={handleWorkingHourModalOnConfirmed}
			/>
			<div className={classes['createHospitalNew']}>
				<div className={classes['createHospitalNew-section']}>
					<div
						className={
							classes['createHospitalNew-sectionFields'] +
							' ' +
							classes['createHospitalNew-spacingTextFields']
						}
					>
						<div className={classes['createHospitalNew-hospitalNameField']}>
							<TextField
								value={name}
								onChange={(e) => setName(e.target.value)}
								color="primary"
								variant="labelled"
								label="Hospital Name:"
							/>
						</div>
						<TimezoneSelect setValue={setTimeZone} />
						<TextField
							placeholder="Address 1"
							label="Address Line 1:"
							variant="labelled"
							value={addressLine1}
							onChange={(e) => setAddressLine1(e.target.value)}
						/>
						<TextField
							placeholder="Address 2"
							label="Address Line 2:"
							variant="labelled"
							value={addressLine2}
							onChange={(e) => setAddressLine2(e.target.value)}
						/>
						<TextField
							placeholder="Ohara"
							label="City:"
							variant="labelled"
							value={city}
							onChange={(e) => setCity(e.target.value)}
						/>
						<TextField
							placeholder="Texas"
							label="State:"
							variant="labelled"
							value={state}
							onChange={(e) => setState(e.target.value)}
						/>
						<TextField
							placeholder="P. O. Box 1102"
							label="Postal Code:"
							variant="labelled"
							value={postalCode}
							onChange={(e) => setPostalCode(e.target.value)}
						/>
					</div>
				</div>
				<div className={classes['createHospitalNew-section']}>
					<Typography weight="bold" className={classes['createHospitalNew-sectionTitle']}>
						Basic Info
					</Typography>
					<div className={classes['createHospitalNew-sectionFields']}>
						<div className={classes['createHospitalNew-phoneField']}>
							<TextField
								value={phoneNumber}
								onChange={(e) => setPhoneNumber(e.target.value)}
								variant="labelled"
								label="Phone:"
								placeholder="+1 55 55 55 555"
							/>
						</div>
						<div className={classes['createHospitalNew-employeeField']}>
							<TextField
								value={employeesAmount}
								onChange={(e) => setEmployeesAmount(e.target.valueAsNumber)}
								variant="labelled"
								label="Employees:"
								type="number"
							/>
						</div>
						<div className={classes['createHospitalNew-emailField']}>
							<TextField
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								variant="labelled"
								type="email"
								label="Email:"
								placeholder="contact@example.com"
							/>
						</div>
						<div>
							<BuddyPunchLocationSelect value={buddyPunchId} setValue={setBuddyPunchId} />
						</div>
					</div>
				</div>
				<div className={classes['createHospitalNew-section']}>
					<Typography weight="bold" className={classes['editHospitalNew-sectionTitle']}>
						Cancellation Policy
					</Typography>
					<div className={classes['editHospitalNew-sectionFields']}>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="Before Dispatch:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={beforeDispatch}
								onChange={(e) => setBeforeDispatch(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="After Dispatch:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={afterDispatch}
								onChange={(e) => setAfterDispatch(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="Before Treatment:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={beforeTreatment}
								onChange={(e) => setBeforeTreatment(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['editHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="During Treatment:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								defaultSymbolIndex={1}
								min={0}
								value={duringTreatment}
								onChange={(e) => setDuringTreatment(e.target.valueAsNumber)}
							/>
						</div>
					</div>
				</div>
				<div className={classes['createHospitalNew-section']}>
					<Typography weight="bold" className={classes['createHospitalNew-sectionTitle']}>
						Pricing Per Hour
					</Typography>
					<div className={classes['createHospitalNew-sectionFields']}>
						<div className={classes['createHospitalNew-quickbooksField']}>
							<TextField
								variant="labelled"
								label="Quickbooks ID"
								placeholder="123465798"
								value={payoutId}
								onChange={(e) => setPayoutId(e.target.value)}
							/>
						</div>
						<div className={classes['createHospitalNew-pricingPerHourField']}>
							<TextField
								variant="labelled"
								label="Pricing per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={regularRate}
								onChange={(e) => setRegularRate(e.target.valueAsNumber)}
							/>
						</div>
						<div className={classes['createHospitalNew-overtimePerHourField']}>
							<TextField
								variant="labelled"
								label="Overtime per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={overtimeRate}
								onChange={(e) => setOvertimeRate(e.target.valueAsNumber)}
								defaultSymbolIndex={overtimeRateTypeId}
								onSymbolChange={(value) => handleSymbolChange('overtime', value)}
							/>
						</div>
						<div className={classes['createHospitalNew-weekendPerHourField']}>
							<TextField
								variant="labelled"
								label="Weekend per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={weekendRate}
								defaultSymbolIndex={weekendRateTypeId}
								onChange={(e) => setWeekendRate(e.target.valueAsNumber)}
								onSymbolChange={(value) => handleSymbolChange('weekend', value)}
							/>
						</div>
						<div className={classes['createHospitalNew-holidayPerHourField']}>
							<TextField
								variant="labelled"
								label="Holiday per Hour:"
								type="number"
								numberSymbol={NUMBER_SYMBOL}
								min={0}
								value={holidayRate}
								defaultSymbolIndex={holidayRateTypeId}
								onChange={(e) => setHolidayRate(e.target.valueAsNumber)}
								onSymbolChange={(value) => handleSymbolChange('holiday', value)}
							/>
						</div>
					</div>
				</div>
				<div className={classes['createHospitalNew-section']} style={{ borderBottom: 'none' }}>
					<Typography weight="bold" className={classes['createHospitalNew-sectionTitle']}>
						Working Hours
					</Typography>
					<div className={classes['createHospitalNew-sectionFields']}>
						{workingHoursFactory.days.map((item, index) => (
							<div
								onClick={() => handleWorkingHourFieldClick(index)}
								className={classes['createHospitalNew-workingHoursField']}
								key={item.day}
								style={{ cursor: 'pointer' }}
							>
								<WorkingHourInputField
									day={item.day}
									start={item.startTime}
									end={item.endTime}
									onChange={(value) => {
										const workingHours = { ...workingHoursFactory };
										workingHours.days[index].startTime = value.start;
										workingHours.days[index].endTime = value.end;

										setWorkingHoursFactory(workingHours);
									}}
									disabled
									prefix
								/>
							</div>
						))}
					</div>
				</div>

				<div className={classes['createHospitalNew-footer']}>
					<Button
						variant="standard"
						color="primary"
						title="Create Hospital"
						style={{
							marginRight: '1rem',
						}}
						onClick={createHospital}
					/>
				</div>
			</div>
		</>
	);
};

export default HospitalNewScreen;
